import React, { useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import {
  Box,
  ContentLayout,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { DashboardType, QuickSightDashboard } from "./QuicksightDashboard";
import PageHeader from "../PageHeader";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { DARK_THEME } from "../../constants";
import useDocumentTitle from "../../hooks/useDocumentTitle";

const PRODUCTS: {
  value: string;
  label: string;
  dashboardType: DashboardType;
}[] = [
  {
    value: "tvod",
    label: "TVOD",
    dashboardType: "royalty_recon_tvod",
  },
  {
    value: "channels",
    label: "Channels",
    dashboardType: "royalty_recon_channels",
  },
];

const BASE_PATH = "/dashboard/royalty-recon";

const RoyaltyReconDashboard: React.FC = () => {
  const { product: productParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const [, setShareHandler] = useState<(() => Promise<string>) | null>(null);

  const product = PRODUCTS.find((p) => p.value === productParam) || null;
  useDocumentTitle(
    product
      ? `${product.label} | Royalty Recon Dashboard`
      : "Royalty Recon Dashboards"
  );

  if (productParam && !PRODUCTS.some((p) => p.value === productParam)) {
    return <Navigate to={BASE_PATH} replace />;
  }

  const handleProductChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete("filters");
    const search = params.toString();
    navigate(`${BASE_PATH}/${value}${search ? `?${search}` : ""}`);
  };

  const selectOptions: SelectProps.Option[] = PRODUCTS.map((p) => ({
    value: p.value,
    label: p.label,
  }));

  return (
    <DarkModeContext.Provider value={darkMode}>
      <StageContext.Provider value={Stage.Prod}>
        <PageHeader setDarkMode={setDarkMode} />
        <AppLayout
          content={
            <ContentLayout
              defaultPadding
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: "240px" }}>
                    <Header variant="h1">Royalty Recon Dashboards</Header>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Select
                      selectedOption={
                        product
                          ? { value: product.value, label: product.label }
                          : null
                      }
                      onChange={({ detail }) =>
                        handleProductChange(
                          detail.selectedOption.value as string
                        )
                      }
                      options={selectOptions}
                      placeholder="Select a dashboard"
                      ariaLabel="Select Royalty Recon dashboard"
                    />
                  </div>
                </div>
              }
            >
              <SpaceBetween size="xl">
                {product ? (
                  <QuickSightDashboard
                    stage={Stage.Prod}
                    dashboardType={product.dashboardType}
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    setShareHandler={setShareHandler}
                  />
                ) : (
                  <Box
                    padding="l"
                    color="text-status-inactive"
                    textAlign="center"
                  >
                    Select a dashboard from the dropdown above to get started.
                  </Box>
                )}
              </SpaceBetween>
            </ContentLayout>
          }
          contentType="default"
          headerSelector="#h"
          maxContentWidth={1700}
          navigationHide
          toolsHide
        />
      </StageContext.Provider>
    </DarkModeContext.Provider>
  );
};

export default RoyaltyReconDashboard;
